import omitBy from 'lodash/omitBy';
import isUndefined from 'lodash/isUndefined';
import isNil from 'lodash/isNil';

import {isPersistedCustomProperty} from 'common/widgets/custom-property-modal/util';
import {reporter} from 'common/report/error-reporting';
import {MetricType} from 'common/query-builder/query-block/constants';
import {FUNNELS_MATH_TYPES, RETENTION_MATH_TYPES, XAU_MATH_TYPES, XAU_ONLY_MATH_TYPES} from 'common/report/constants';
import {FUNNELS_TIME_TO_CONVERSION, PAGE_DURATION} from 'common/query-builder/query-block/properties-menu/constants';
import {FunnelOrder} from 'common/report/funnels/models/constants';
import {FunnelBookmark} from 'common/report/funnels/models/types';
import {FlowsBookmark} from 'common/report/flows/models/types';
import {RetentionBookmarkParams} from 'common/report/retention/util/types';
import type {MetricsResource} from 'common/report/model-resources/metrics';
import {getResolvedFormulaShowClause} from 'common/util/formulas';
import {Behavior} from 'common/types/reports/query';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';

import {
  BehaviorMetric,
  BehaviorShowClause as BehaviorShowClauseType,
  FormulaShowClause as FormulaShowClauseType,
  GroupClause as GroupClauseType,
  InsightsBookmarkParams,
  InsightsResourceType,
  isInsightsBookmarkParams,
  MetricBehavior,
  MetricMetadata,
  ShowClause as ShowClauseType,
  ShowClauseFormula,
} from '../util/types';

export function sanitizeIfCustomProperty(property: BehaviorMetric['measurement']['property']) {
  if (!property.customPropertyId) {
    return property;
  }

  const {customProperty, ...rest} = property;
  return {
    ...rest,
    // customProperty (custom property definition) should not be passed as an attr for persisted
    // custom properties. However, old bookmark did store customProperty for persisted custom
    // properties, which we remove below.
    ...(!isPersistedCustomProperty(property.customPropertyId) && customProperty ? {customProperty} : {}),
  };
}

export function isBehaviorShowClause(showClause: ShowClauseType): showClause is BehaviorShowClauseType {
  if (`behavior` in showClause && `measurement` in showClause) {
    return true;
  }

  if (`behavior` in showClause) {
    reporter.error(`Non-behavior show clause has unexpected "behavior" key`, {showClause});
  }
  return false;
}

export function isFormulaShowClause(showClause: ShowClauseType): showClause is FormulaShowClauseType {
  return `formula` in showClause;
}

export function getBehaviorShowClauses(showClauses: ShowClauseType[]): BehaviorShowClauseType[] {
  if (Array.isArray(showClauses) === false) {
    return [];
  }

  return showClauses.filter(isBehaviorShowClause);
}

const findMatchingMetric = (metadata: MetricMetadata, path: string[]) => metadata.metric_key === path[0];

export const findShowClauseIndex = (meta: MetricMetadata[], path: string[]) => {
  return meta.findIndex(
    (item) => findMatchingMetric(item, path) || item[`sub_queries`]?.some((item) => findMatchingMetric(item, path)),
  );
};

/**
 * Get BehaviorShowClauses plus any that are in FormulaShowClause.formula.referencedMetrics
 * Note: this function is similar to the method getExpandedBehaviorClauses on ShowSection
 * in ../section.ts (which operates on class types)
 */
export function getExpandedBehaviorShowClauses({
  showClauses,
  metricsResource,
  behaviorsResource,
}: {
  showClauses: ShowClauseType[];
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
}): BehaviorShowClauseType[] {
  if (Array.isArray(showClauses) === false) {
    return [];
  }

  return showClauses
    .map((showClause) => {
      if (isBehaviorShowClause(showClause)) {
        return showClause;
      } else {
        const resolved = getResolvedFormulaShowClause({
          formulaShowClause: showClause,
          metricsResource,
          behaviorsResource,
        });
        return resolved?.formula?.referencedMetrics || [];
      }
    })
    .flat();
}

export function hasGroupByCohort(group: GroupClauseType[]): boolean {
  return group.some((groupClause) => groupClause.cohorts);
}

export function hasGroupByDuration(group: GroupClauseType[]): boolean {
  return group.some((groupClause) => groupClause.value === PAGE_DURATION.value);
}

export function hasGroupByUser(group: GroupClauseType[]): boolean {
  return group.some((groupClause) => groupClause.resourceType === InsightsResourceType.People);
}

export function hasRolling(showClauses: ShowClauseType[]): boolean {
  return showClauses.some((clause) => clause.measurement?.rolling);
}

export function hasCumulative(showClauses: ShowClauseType[]): boolean {
  return showClauses.some((clause) => clause.measurement?.cumulative);
}

export function getNumerOfEventComparisons(showClause: ShowClauseType): number {
  if (!showClause) return 0;

  const behaviors = isBehaviorShowClause(showClause) ? showClause.behavior?.behaviors : [];
  return (behaviors || []).reduce((acc, beh) => {
    if (`behaviors` in beh) {
      return acc + 1;
    }
    return acc;
  }, 0);
}

export function hasAnyOrderFunnel(showClauses: ShowClauseType[]) {
  return (
    showClauses.some((clause) => isBehaviorShowClause(clause) && clause.behavior.type === MetricType.Funnel) &&
    showClauses.some(
      (clause) =>
        isBehaviorShowClause(clause) &&
        clause.behavior.behaviors?.some((behavior) => behavior.funnelOrder === FunnelOrder.Any),
    )
  );
}

export function hasEventComparison(showClauses: ShowClauseType[]) {
  return showClauses.some(
    (clause) =>
      isBehaviorShowClause(clause) &&
      clause.behavior.type === MetricType.Funnel &&
      clause.behavior.behaviors?.some((behavior) => Array.isArray(behavior.behaviors)),
  );
}

export function hasEventBreakdown(groupClauses: GroupClauseType[]) {
  return groupClauses.some((clause) => clause.resourceType === InsightsResourceType.Events);
}

export function hasRetentionFrequency(showClauses: ShowClauseType[]) {
  return showClauses.some(
    (clause) => isBehaviorShowClause(clause) && clause.behavior.type === MetricType.RetentionFrequency,
  );
}

export function hasTTC(showClauses: ShowClauseType[]) {
  return showClauses.some(
    (clause) =>
      isBehaviorShowClause(clause) &&
      clause.measurement.property &&
      clause.measurement.property.name === `$time_to_conversion`,
  );
}

export function getFormulaShowClauses(showClauses: ShowClauseType[]): FormulaShowClauseType[] {
  return showClauses.filter(isFormulaShowClause);
}

export function hasSavedFormula(params: InsightsBookmarkParams) {
  return params.sections.show.some((clause) => {
    if (!isFormulaShowClause(clause)) {
      return false;
    }

    return Boolean(clause.formula.id);
  });
}

export function hasSavedBehavior(params: InsightsBookmarkParams) {
  return params.sections.show.some((clause) => {
    if (!isBehaviorShowClause(clause)) {
      return false;
    }

    return Boolean(clause.behavior.id);
  });
}

export function includesSavedBehaviorId({
  params,
  behaviorId,
  metricsResource,
  behaviorsResource,
}: {
  params: InsightsBookmarkParams;
  behaviorId: number;
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
}) {
  return params.sections.show.some((clause) => {
    if (isFormulaShowClause(clause)) {
      const referencedMetrics =
        getResolvedFormulaShowClause({
          formulaShowClause: clause,
          metricsResource,
          behaviorsResource,
        })?.formula?.referencedMetrics || [];
      return referencedMetrics.some((metric) => metric.behavior.id === behaviorId);
    }
    return clause.behavior.id === behaviorId;
  });
}

/**
 * Hydrate bookmark with saved behaviors to the real definitions
 * precondition: behaviors are already fetched
 */
export function hydrateSavedBehaviors(
  params: InsightsBookmarkParams,
  behaviors: BehaviorsResource,
): InsightsBookmarkParams {
  if (!isInsightsBookmarkParams(params)) {
    return params;
  }
  if (!hasSavedBehavior(params)) {
    return params;
  }
  return {
    ...params,
    sections: {
      ...params.sections,
      show: params.sections.show.map((show) => {
        if (
          isBehaviorShowClause(show) &&
          show.behavior.id &&
          [MetricType.Retention, MetricType.Funnel].includes(show.behavior.type)
        ) {
          // If the existing behavior definition is present, respect it
          if (!isNil(show.behavior.behaviors)) {
            return show;
          }
          const savedBehavior = behaviors.getOne(show.behavior.id).value;
          if (!savedBehavior) {
            return show;
          }
          return {
            ...show,
            behavior: {
              ...savedBehavior.definition.behavior,
              id: savedBehavior.id,
              name: savedBehavior.name,
              type: savedBehavior.type,
            } as MetricBehavior,
          };
        }
        return show;
      }),
    },
  };
}

enum CustomPropertyReferenceLocation {
  MetricAggregation = `metric_aggregation`,
  MetricFilter = `metric_filter`,
  MetricFilterBehavior = `metric_filter_behavior`,
  Breakdown = `breakdown`,
  BreakdownBehavior = `breakdown_behavior`,
  Filter = `filter`,
  FilterBehavior = `filter_behavior`,
}

type CustomPropertyReference = {
  referenceLocation: CustomPropertyReferenceLocation;
  customPropertyId: number | string;
};

/**
 * NOTE: This function doesn't dedupe custom properties, since it's used to determine total number of borrowed
 * properties in the report and the location of each usage.
 */
export function getCustomPropertyReferences(params: InsightsBookmarkParams): CustomPropertyReference[] {
  const {show, group, filter} = params.sections;
  const customPropertyReferences: CustomPropertyReference[] = [];

  const addReferencesFromBehavior = ({
    behavior,
    referenceLocation,
  }: {
    behavior: Behavior;
    referenceLocation: CustomPropertyReferenceLocation;
  }): void => {
    const behaviorProperty = behavior?.property;
    if (behaviorProperty && `customPropertyId` in behaviorProperty) {
      customPropertyReferences.push({
        referenceLocation,
        customPropertyId: behaviorProperty.customPropertyId,
      });
    }
  };

  const showBehaviorClauses = show.filter(isBehaviorShowClause);
  showBehaviorClauses.forEach((behaviorClause) => {
    behaviorClause.behavior.filters?.forEach((clause) => {
      if (clause.customPropertyId) {
        customPropertyReferences.push({
          referenceLocation: CustomPropertyReferenceLocation.MetricFilter,
          customPropertyId: clause.customPropertyId,
        });
      }

      addReferencesFromBehavior({
        behavior: clause.behavior,
        referenceLocation: CustomPropertyReferenceLocation.MetricFilterBehavior,
      });
    });

    const measurementCustomPropertyId = behaviorClause.measurement.property?.customPropertyId;
    if (measurementCustomPropertyId) {
      customPropertyReferences.push({
        referenceLocation: CustomPropertyReferenceLocation.MetricAggregation,
        customPropertyId: measurementCustomPropertyId,
      });
    }
  });

  group.forEach((clause) => {
    if (clause.customPropertyId) {
      customPropertyReferences.push({
        referenceLocation: CustomPropertyReferenceLocation.Breakdown,
        customPropertyId: clause.customPropertyId,
      });
    }
    addReferencesFromBehavior({
      behavior: clause.behavior,
      referenceLocation: CustomPropertyReferenceLocation.BreakdownBehavior,
    });
  });

  filter.forEach((clause) => {
    if (clause.customPropertyId) {
      customPropertyReferences.push({
        referenceLocation: CustomPropertyReferenceLocation.Filter,
        customPropertyId: clause.customPropertyId,
      });
    }
    addReferencesFromBehavior({
      behavior: clause.behavior,
      referenceLocation: CustomPropertyReferenceLocation.FilterBehavior,
    });
  });

  return customPropertyReferences;
}

export function isValidFormula(formula: ShowClauseFormula): boolean {
  if (formula.id) {
    return true;
  }

  return Boolean(formula && formula.name && formula.definition);
}

export function transformBookmarkToMultiMetricBookmark(bookmarkParams: InsightsBookmarkParams): InsightsBookmarkParams {
  // defensive programming, just in case the params pass in is falsy
  if (!bookmarkParams?.sections) return bookmarkParams;
  return {
    ...bookmarkParams,
    sections: {
      ...bookmarkParams.sections,
      cohorts: [],
      group: moveCohortToGroupSection(bookmarkParams),
      show: bookmarkParams.sections.show.map((show) => {
        if (isFormulaShowClause(show)) {
          return {
            ...(show.isHidden ? {isHidden: true} : {}),
            ...(show.isExpanded ? {isExpanded: true} : {}),
            formula: {...show.formula},
            display: {...show.display},
            measurement: {...show.measurement},
          };
        } else {
          const {behavior, display, measurement} = transformShowToBehaviorMetric(show);
          return {
            ...(show.isHidden ? {isHidden: true} : {}),
            ...(show.isExpanded ? {isExpanded: true} : {}),
            behavior: {
              type: MetricType.Event,
              resourceType: behavior.resourceType,
              ...omitBy(behavior, isUndefined),
            },
            display: {
              ...(display?.axis && {axis: display.axis}),
              ...omitBy(display, isUndefined),
            },
            measurement: {
              math: measurement.math,
              ...omitBy(measurement, isUndefined),
            },
          };
        }
      }),
    },
  };
}

export function moveCohortToGroupSection(bookmarkParams: InsightsBookmarkParams) {
  const cohortsSection = bookmarkParams.sections?.cohorts || [];
  // Place cohorts at the top level in group section
  const cohorts = [];
  cohortsSection.forEach((cohort) => {
    cohorts.push(cohort);
  });

  const groupSection = bookmarkParams.sections?.group || [];
  if (cohorts.length > 0) {
    const value = cohorts.map((cohort) => cohort.name);
    const groupByCohorts: GroupClauseType = {
      value,
      cohorts,
      resourceType: InsightsResourceType.Events,
    };
    return [groupByCohorts, ...groupSection];
  }

  return groupSection;
}

export function transformShowToBehaviorMetric(show: BehaviorShowClauseType): BehaviorMetric {
  if (show.behavior && show.measurement) {
    return {
      behavior: show.behavior,
      display: show.display,
      measurement: show.measurement,
    };
  }
  const {
    math,
    perUserAggregation,
    property,
    rolling,
    cumulative,
    segmentMethod,
    multiAttribution,
    search,
    resourceType,
    profileType,
    value,
    filter,
    filterDeterminer,
    dataGroupId,
    dataset,
    datasetId,
    projectId,
    customBucket,
  } = show;
  // for original show clause value, it could only represent event or custom event or cohort
  const {name, custom, id, raw_cohort: rawCohort, renamed, resourceType: valueResourceType} = value || {};
  const type = custom
    ? MetricType.CustomEvent
    : valueResourceType === InsightsResourceType.Cohorts || resourceType === InsightsResourceType.Cohorts
      ? MetricType.Cohort
      : resourceType === InsightsResourceType.People
        ? MetricType.People
        : MetricType.Event;

  return {
    measurement: {
      math,
      property,
      cumulative,
      perUserAggregation,
      rolling,
      segmentMethod,
      multiAttribution,
    },
    behavior: {
      type,
      id,
      name,
      renamed,
      raw_cohort: rawCohort,
      search,
      resourceType,
      profileType,
      filters: filter,
      filtersDeterminer: filterDeterminer,
      dataGroupId,
      dataset,
      datasetId,
      projectId,
      customBucket,
    },
  };
}

export function getMetricTypes(showClauses: ShowClauseType[]): Set<MetricType> {
  const metricTypes = new Set<MetricType>();
  showClauses.forEach((showClause) => {
    if (isBehaviorShowClause(showClause)) {
      metricTypes.add(showClause.behavior.type);
    } else if (isFormulaShowClause(showClause)) {
      metricTypes.add(MetricType.Formula);
    }
  });
  return metricTypes;
}

export function isFunnelShowClause(show: ShowClauseType): show is BehaviorShowClauseType {
  return isBehaviorShowClause(show) && show.behavior?.type === MetricType.Funnel;
}

export function isFunnelConversionShowClause(show: ShowClauseType): boolean {
  return (
    isBehaviorShowClause(show) &&
    show.behavior?.type === MetricType.Funnel &&
    [
      FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE,
      FUNNELS_MATH_TYPES.CONVERSION_RATE_TOTAL,
      FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION,
    ].includes(show.measurement.math as FUNNELS_MATH_TYPES)
  );
}

export function isFunnelTimeToConvertShowClause(show: ShowClauseType): boolean {
  return (
    isBehaviorShowClause(show) &&
    ((show.behavior?.type === MetricType.Funnel &&
      show.measurement.property?.name === FUNNELS_TIME_TO_CONVERSION.value) ||
      ((show.behavior?.type === MetricType.Event || show.behavior?.type === MetricType.CustomEvent) &&
        show.measurement.property?.name === PAGE_DURATION.value))
  );
}

export function isRetentionShowClause(show: ShowClauseType): show is BehaviorShowClauseType {
  return (
    isBehaviorShowClause(show) && [MetricType.Retention, MetricType.RetentionFrequency].includes(show.behavior?.type)
  );
}

export function isRetentionRateShowClause(show: ShowClauseType): boolean {
  return (
    isBehaviorShowClause(show) &&
    [MetricType.Retention, MetricType.RetentionFrequency].includes(show.behavior?.type) &&
    show.measurement.math === RETENTION_MATH_TYPES.RETENTION_RATE
  );
}

export function isXauShowClause(show: ShowClauseType): boolean {
  return isBehaviorShowClause(show) && XAU_ONLY_MATH_TYPES.includes(show.measurement.math as XAU_MATH_TYPES);
}

export function isCohortsShowClause(show: ShowClauseType): boolean {
  return (
    isBehaviorShowClause(show) &&
    [InsightsResourceType.Cohorts, InsightsResourceType.People].includes(show.behavior?.resourceType)
  );
}

export function isFunnelTimeToConvertMetric(bookmark: InsightsBookmarkParams): boolean {
  return bookmark.sections.show.every(isFunnelTimeToConvertShowClause);
}

export function isConversionOrRetentionRateMetric(bookmark: InsightsBookmarkParams): boolean {
  return bookmark.sections.show.every((show) => isFunnelConversionShowClause(show) || isRetentionRateShowClause(show));
}

export function hasOneFunnelMetric(bookmark: InsightsBookmarkParams): boolean {
  const filteredShowClauses = bookmark.sections.show.filter((show) => !show.isHidden);
  return filteredShowClauses.length === 1 && isFunnelShowClause(filteredShowClauses[0]);
}

export function hasFunnelMetric(bookmark: InsightsBookmarkParams): boolean {
  return bookmark.sections.show.some(isFunnelShowClause);
}

export function hasFunnelConversionMetric(bookmark: InsightsBookmarkParams): boolean {
  return bookmark.sections.show.some(isFunnelConversionShowClause);
}

export function hasFunnelTimeToConvertMetric(bookmark: InsightsBookmarkParams): boolean {
  return bookmark.sections.show.some(isFunnelTimeToConvertShowClause);
}

export function hasRetentionMetric(bookmark: InsightsBookmarkParams): boolean {
  return bookmark.sections.show.some(isRetentionShowClause);
}

export function hasRetentionRateMetric(bookmark: InsightsBookmarkParams): boolean {
  return bookmark.sections.show.some(isRetentionRateShowClause);
}

export function hasXauMetric(bookmark: InsightsBookmarkParams): boolean {
  return bookmark.sections.show.some(isXauShowClause);
}

export function hasCohortsOverTimeMetric(bookmark: InsightsBookmarkParams): boolean {
  return bookmark.sections.show.some(isCohortsShowClause);
}

export function isFunnelOnlyInsightsBookmark(
  bookmark: InsightsBookmarkParams | FunnelBookmark | FlowsBookmark | RetentionBookmarkParams,
): boolean {
  if (!isInsightsBookmarkParams(bookmark) || bookmark.sections.show.length === 0) {
    return false;
  }
  // TODO(CC): Handle formula - If the formula's referenced metrics are funnel metrics only, return true
  const filteredShowClauses = bookmark.sections.show.filter((show) => !show.isHidden);
  return filteredShowClauses.every(isFunnelShowClause);
}

export function isRetentionOnlyInsightsBookmark(
  bookmark: InsightsBookmarkParams | FunnelBookmark | FlowsBookmark | RetentionBookmarkParams,
): boolean {
  if (!isInsightsBookmarkParams(bookmark) || bookmark.sections.show.length === 0) {
    return false;
  }
  // TODO(CC): Handle formula - If the referenced metrics are retention metrics only, return true
  const filteredShowClauses = bookmark.sections.show.filter((show) => !show.isHidden);
  return filteredShowClauses.every(isRetentionShowClause);
}

export function hasFunnelTimeToConvertBreakdownBookmark(bookmark: InsightsBookmarkParams): boolean {
  return bookmark.sections.group.some((group) => group.value === FUNNELS_TIME_TO_CONVERSION.value);
}

export function getMaxStepIndex(showClauses: ShowClauseType[]): number {
  return (
    Math.max(
      ...showClauses.map((showClause) => {
        if (isFormulaShowClause(showClause)) {
          return 0;
        }
        if (isBehaviorShowClause(showClause) && Array.isArray(showClause.behavior.behaviors)) {
          return showClause.behavior.behaviors.length;
        }
        return 1;
      }),
    ) - 1
  );
}
